import React, { useState, useEffect } from "react";
import {
  BottomNavigation,
  BottomNavigationAction,
  Box,
  Typography,
  useTheme,
  Fab,
 
} from "@mui/material";
import {
  Home as HomeIcon,
  School as StudentsIcon,
  AddCircle as AddCircleIcon,
  CurrencyExchange as TransactionsIcon,
  Refresh as RefresherIcon,
  MenuBook as CoursesIcon,
  OndemandVideo as LessonsIcon,
  AccountBalanceWallet as FeesIcon,
  Add as AddIcon,
  Close as CloseIcon,
  Person as UserActivation,
} from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import { getPrimaryBlendedColor } from "../../utils/themeColor";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../pages/authentication/AuthContext";

const MobileBottomNav = ({ toggleMobileDrawer }) => {
  const [addRotated, setAddRotated] = useState(false);
  const [speedDialOpen, setSpeedDialOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const { user } = useAuth();
  const userRole = user?.role || null;

  // Determine selected index based on current route with exact matching
  const getSelectedIndex = () => {
    const path = location.pathname;

    // Get the current user's navigation items
    const currentUserItems = {
      admin: [
        { route: "/admin" },
        { route: "/admin/students" },
        { route: null }, // Add button
        { route: "/admin/transactions" },
        { route: "/admin/profile" },
      ],
      student: [
        { route: "/student" },
        { route: "/student/my-courses" },
        { route: null }, // Add button
        { route: "/student/payments" },
        { route: "/student/profile" },
      ],
      instructor: [
        { route: "/instructor" },
        { route: "/instructor/my-courses" },
        { route: null }, // Add button
        { route: "/instructor/mylessons" },
        { route: "/instructor/profile" },
      ],
      manager: [
        { route: "/manager" },
        { route: "/manager/students" },
        { route: null }, // Add button
        { route: "/manager/courses" },
        { route: "/manager/profile" },
      ],
    };

    const userItems = currentUserItems[userRole];
    if (!userItems) return 0;

    // Find exact match first
    const exactMatch = userItems.findIndex((item) => item.route === path);
    if (exactMatch !== -1) return exactMatch;

    // If no exact match, find the best partial match
    let bestMatch = 0;
    let bestMatchLength = 0;

    userItems.forEach((item, index) => {
      if (item.route && path.startsWith(item.route)) {
        if (item.route.length > bestMatchLength) {
          bestMatch = index;
          bestMatchLength = item.route.length;
        }
      }
    });

    return bestMatch;
  };

  const [value, setValue] = useState(getSelectedIndex());

  // Update selected index when route changes
  useEffect(() => {
    const newIndex = getSelectedIndex();
    setValue(newIndex);
  }, [location.pathname, userRole]);

  // Handle Add icon rotation and sidebar toggle
  const handleAddClick = () => {
    setAddRotated(true);
    setTimeout(() => setAddRotated(false), 500);
    toggleMobileDrawer();
  };

  // Navigate function with immediate state update and route validation
  const handleNavClick = (index, route) => {
    // Update state immediately for instant visual feedback
    setValue(index);

    // Navigate to the route
    if (route) {
      navigate(route);
    }
  };

  const iconSx = (isSelected, isAddIcon = false) => ({
    fontSize: isAddIcon ? "1.8rem" : "1.4rem",
    transform: isAddIcon && addRotated ? "rotate(180deg)" : "rotate(0)",
    transition: isAddIcon ? "transform 0.3s ease-in-out" : "color 0.2s ease",
    color: isSelected ? theme.palette.primary.main : "#64748b",
  });

  const renderNavItems = (role) => {
    const items = {
      admin: [
        { label: "Home", icon: <HomeIcon />, route: "/admin" },
        { label: "Students", icon: <StudentsIcon />, route: "/admin/students" },
        { label: "", icon: <AddCircleIcon />, action: handleAddClick },
        {
          label: "Payments",
          icon: <TransactionsIcon />,
          route: "/admin/transactions",
        },
        { label: "Profile", icon: <UserActivation />, route: "/admin/profile" },
      ],
      student: [
        { label: "Home", icon: <HomeIcon />, route: "/student" },
        {
          label: "Enrollments",
          icon: <CoursesIcon />,
          route: "/student/my-courses",
        },
        { label: "", icon: <AddCircleIcon />, action: handleAddClick },
        { label: "Payments", icon: <FeesIcon />, route: "/student/payments" },
        { label: "Profile", icon: <UserActivation />, route: "/student/profile" },
      ],
      instructor: [
        { label: "Home", icon: <HomeIcon />, route: "/instructor" },
        {
          label: "Courses",
          icon: <CoursesIcon />,
          route: "/instructor/my-courses",
        },
        { label: "", icon: <AddCircleIcon />, action: handleAddClick },
        {
          label: "Lessons",
          icon: <LessonsIcon />,
          route: "/instructor/mylessons",
        },
        { label: "Profile", icon: <UserActivation />, route: "/instructor/profile" },
      ],
      manager: [
        { label: "Home", icon: <HomeIcon />, route: "/manager" },
        {
          label: "Students",
          icon: <StudentsIcon />,
          route: "/manager/students",
        },
        { label: "", icon: <AddCircleIcon />, action: handleAddClick },
        { label: "Courses", icon: <CoursesIcon />, route: "/manager/courses" },
        { label: "Profile", icon: <UserActivation />, route: "/manager/profile" },
      ],
    };

    return items[role]?.map((item, index) => {
      const isSelected = value === index;
      const isAddButton = item.label === "";

      // For the add button, render a floating action button
      if (isAddButton) {
        return (
         <Box
  key="add-button"
  sx={{
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 64,
    
    boxShadow: "0 -1px 3px rgba(0,0,0,0.1)" // 👈 top shadow
  }}
>

            <Fab
              size="medium"
              onClick={handleAddClick}
              sx={{
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                color: "white",
                transform: addRotated ? "rotate(180deg)" : "rotate(0)",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
                "&:hover": {
                  transform: addRotated
                    ? "rotate(180deg) scale(1.1)"
                    : "scale(1.1)",
                  boxShadow: "0 6px 25px rgba(0,0,0,0.2)",
                },
                "&:active": {
                  transform: addRotated
                    ? "rotate(180deg) scale(0.95)"
                    : "scale(0.95)",
                },
              }}
            >
              <AddIcon sx={{ color: "white" }} />
            </Fab>
          </Box>
        );
      }

      return (
        <BottomNavigationAction
          key={item.label}
          label={
            <Typography
              variant="caption"
              sx={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                fontSize: "0.7rem",
                fontWeight: isSelected ? 600 : 400,
                color: isSelected ? theme.palette.primary.main : "#64748b",
                transition: "all 0.2s ease",
                marginTop: 0.5,
              }}
            >
              {item.label}
            </Typography>
          }
          icon={React.cloneElement(item.icon, {
            sx: {
              ...iconSx(isSelected, false),
              // Force immediate color change
              color: isSelected
                ? `${theme.palette.primary.main} !important`
                : "#64748b !important",
            },
          })}
          onClick={() => handleNavClick(index, item.route)}
          sx={{
            flexDirection: "column",
            alignItems: "center",
            minWidth: 64,
            paddingTop: 1,
            paddingBottom: 0.5,
            position: "relative",
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: "rgba(0,0,0,0.04)",
            },
            "&.Mui-selected": {
              color: theme.palette.primary.main,
              "& .MuiBottomNavigationAction-label": {
                color: theme.palette.primary.main,
              },
            },
            // Active indicator with immediate update
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: "50%",
              transform: "translateX(-50%)",
              width: isSelected ? "32px" : "0px",
              height: "3px",
              backgroundColor: isSelected
                ? `${theme.palette.primary.main} !important`
                : "transparent",
              borderRadius: "0 0 3px 3px",
              transition: "width 0.2s ease, background-color 0.2s ease",
            },
          }}
        />
      );
    });
  };

  const navColor = getPrimaryBlendedColor(theme.palette.primary.main);

  return (
    <Box sx={{ position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 1000 }}>
      <BottomNavigation
        showLabels
        value={value}
        onChange={(event, newValue) => setValue(newValue)}
        sx={{
          backgroundColor: navColor,
          borderTop: "1px solid rgba(0,0,0,0.08)",
          paddingY: 0.5,
          paddingBottom: 'calc(4px + env(safe-area-inset-bottom))',
          minHeight: 70,
          boxShadow: "0 -2px 20px rgba(0,0,0,0.1)",
          "& .MuiBottomNavigationAction-root": {
            "&.Mui-selected": {
              paddingTop: 1,
            },
          },
        }}
      >
        {userRole && renderNavItems(userRole)}
      </BottomNavigation>
    </Box>
  );
};

export default MobileBottomNav;
